import rules from "@/utils/inputRules";
import {isFalse, isTrue} from "@/utils/service/DocumentGeneration/answers";
import {PRIVACY_POLICY, REFUND_POLICY} from "@/utils/service/documents";
import {translation} from "@/utils/service/Translations/translations";

export default [
    {
        name: "General Information",
        inputs: [
            {
                model: "company.name",
                type: "businessName",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                label: "Company Code",
                model: "company.code",
                type: "text",
                classes: "col-md-6",
            },
            {
                model: "company.email",
                type: "email",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                label: "Address",
                model: "company.address",
                type: "text",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                model: "company.country",
                type: "country",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                model: "company.phone",
                type: "phone",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                model: "website_url",
                type: "websiteUrl",
                classes: "col-md-6",
                rules: [rules.required, rules.url],
            },
        ],
        actions: [
            {
                component: () => import("@/components/Projects/Project/ProjectGeneralInformationUpdateOnDocumentCreate.vue"),
                params: (self) => ({
                    color: "secondaryDark",
                    text: true,
                    class: "th-mr-12",
                    to: {
                        name: "project_index",
                        params: self?.$route.params,
                    },
                }),
                cta: "Edit project information",
            },
        ]
    },
    {
        name: "Privacy Policy",
        inputs: [
            {
                title: "Does your website have a Privacy Policy?",
                model: "privacyPolicy.use",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => isTrue(model["privacyPolicy.use"]),
                title: "Please insert the link to your Privacy Policy",
                model: "privacyPolicy.link",
                type: "projectDocument",
                policyType: PRIVACY_POLICY,
            },
            {
                condition: (model) => isFalse(model["privacyPolicy.use"]),
                type: "custom",
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                input: {
                    title: `We strongly recommend that you include a Privacy Policy for your website`,
                },
            },
            {
                condition: (model) => isFalse(model["privacyPolicy.use"]),
                model: "privacyPolicy.no_use_create",
                type: "projectDocumentCreateSuggest",
                policyType: PRIVACY_POLICY,
            },
        ],
        validate: (model) =>
            isFalse(model["privacyPolicy.use"]) ||
            rules.required(model["privacyPolicy.link"]) ||
            rules.url(model["privacyPolicy.link"]) ||
            true,
    },
    {
        name: "Dispute Resolution",
        classes: "flex-column",
        alerts: [
            {
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: (self) => ({
                    subtitleNoTrans: true,
                    title:
                        "What are the different types of dispute resolution?",
                    subtitle: `<ul class="th-m-0"><li>${translation.t(
                        "Court litigation is when a judge or jury determines the outcome."
                    )}</li><li>${translation.t(
                        "Arbitration is when an arbitrator, a neutral third party selected by the parties, determines the outcome."
                    )}</li></ul>`,
                }),
            },
        ],
        inputs: [
            {
                title:
                    "How would you like to resolve the disputes with your website users?",
                model: "disputeResolution.type",
                type: "radio",
                default: "court",
                values: {
                    court: "Court litigation",
                    arbitration: "Arbitration",
                },
            },
        ],
    },
    {
        name: "Refund Policy",
        inputs: [
            {
                title:
                    "Do you provide paid services or sell any goods on your website?",
                model: "paymentPolicy.services",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) =>
                    isTrue(model["paymentPolicy.services"]),
                title: "Are your services or goods eligible for a refund?",
                model: "paymentPolicy.refund",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) =>
                    isTrue(model["paymentPolicy.services"]) &&
                    isTrue(model["paymentPolicy.refund"]),
                title: "Do you have a refund policy?",
                model: "paymentPolicy.have_refund_policy",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) =>
                    model["paymentPolicy.services"] === "true" &&
                    model["paymentPolicy.refund"] === "true" &&
                    model["paymentPolicy.have_refund_policy"] === "true",
                title: "Please insert the link to your Refund Policy",
                model: "paymentPolicy.refund_policy_link",
                type: "projectDocument",
                policyType: REFUND_POLICY,
            },
            {
                condition: (model) =>
                    isTrue(model["paymentPolicy.services"]) &&
                    isTrue(model["paymentPolicy.refund"]) &&
                    isFalse(model["paymentPolicy.have_refund_policy"]),
                type: "custom",
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                input: {
                    title: `We strongly recommend that you include a Refund Policy for your website`,
                },
            },
            {
                condition: (model) =>
                    isTrue(model["paymentPolicy.services"]) &&
                    isTrue(model["paymentPolicy.refund"]) &&
                    isFalse(model["paymentPolicy.have_refund_policy"]),
                model: "paymentPolicy.no_use_create",
                type: "projectDocumentCreateSuggest",
                policyType: REFUND_POLICY,
            },
            {
                condition: (model) =>
                    isTrue(model["paymentPolicy.services"]) &&
                    isTrue(model["paymentPolicy.refund"]) &&
                    isFalse(model["paymentPolicy.have_refund_policy"]) &&
                    model["paymentPolicy.no_use_create"] === "skip" ,
                title:
                    "Please select how many days from the purchase your user can ask for refund",
                model: "paymentPolicy.refundRequestDays",
                type: "radio",
                default: "14",
                values: {
                    14: "14 days",
                    30: "30 days",
                    other: "Other",
                },
            },
            {
                condition: (model) =>
                    isTrue(model["paymentPolicy.services"]) &&
                    isTrue(model["paymentPolicy.refund"]) &&
                    isFalse(model["paymentPolicy.have_refund_policy"]) &&
                    model["paymentPolicy.no_use_create"] === "skip" &&
                    model["paymentPolicy.refundRequestDays"] === "other",
                model: "paymentPolicy.refundRequestDays_other",
                type: "text",
                inputType: "number",
                rules: [rules.required, rules.number],
            },
            {
                condition: (model) =>
                    isTrue(model["paymentPolicy.services"]) &&
                    isTrue(model["paymentPolicy.refund"]) &&
                    isFalse(model["paymentPolicy.have_refund_policy"]) &&
                    model["paymentPolicy.no_use_create"] === "skip",
                title: "Please select how many days the refund is processed",
                model: "paymentPolicy.refundProcessDays",
                type: "radio",
                default: "14",
                values: {
                    14: "14 days",
                    30: "30 days",
                    other: "Other",
                },
            },
            {
                condition: (model) =>
                    isTrue(model["paymentPolicy.services"]) &&
                    isTrue(model["paymentPolicy.refund"]) &&
                    isFalse(model["paymentPolicy.have_refund_policy"]) &&
                    model["paymentPolicy.no_use_create"] === "skip" &&
                    model["paymentPolicy.refundProcessDays"] === "other",
                model: "paymentPolicy.refundProcessDays_other",
                type: "text",
                inputType: "number",
                rules: [rules.required, rules.number],
            },
        ],
    },
    {
        name: "User Generated Content",
        alerts: [
            {
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title: "Important",
                    subtitle:
                        'If you use user’s feedback or any received content for promotional or other purposes (e.g. on Facebook, Twitter, Instagram), select "Yes"',
                }),
            },
        ],
        inputs: [
            {
                title:
                    "Can your user generate, send, or post content on your website or social media account (including user submissions)?",
                model: "userGeneratedContent",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
        ],
    },
    {
        name: "Links to Third Party Websites",
        alerts: [
            {
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title: "Important",
                    subtitle:
                        'Even if your website has only the link with your own social media account (e.g. Facebook, Twitter, Instagram), select "Yes"',
                }),
            },
        ],
        inputs: [
            {
                title:
                    "Does your website contain links to Third-Party websites?",
                model: "thirdPartyWebsites",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
        ],
    },
    {
        name: "User accounts",
        inputs: [
            {
                title:
                    "Does your user can create an account on your website?",
                model: "userAccounts",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
        ],
    },
    {
        name: "Additional Clauses",
        inputs: [
            {
                title:
                    "Would you like to add additional clauses to these terms?",
                model: "additional_clauses_use",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => model["additional_clauses_use"] === "true",
                model: "additional_clauses",
                label: "Additional clause",
                type: "custom",
                default: [],
                component: () =>
                    import(
                        "@/components/Documents/TOS/WebsiteTosAdditionalClauseEntry.vue"
                    ),
            },
        ],
    },
];
